import {
	ReactElement,
	useEffect,
	useState
} from "react";
import { NoResults, TableView } from "client/Components";
import { SettingsPage } from "../../SettingsPage";
import { localize } from "client_legacy/ndaw/culture";
import {
	UserLicense,
	UserLicenseStatistics,
	getStatistics
} from "client/Api/FeaturePackages/featurePackageManagement";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import {
	AlertManager,
	useAlerts,
	useForceRerender
} from "client/Hooks";
import { sortBy } from "client_legacy/ndaw/arrayHelpers";
import { hasImplicitDependencyOn } from "client/Misc/reactUtils";
import { getUserLicenseName } from "client/Features/FeaturePackages/misc";

export const UserLicenseSection = (): ReactElement | null => {
	const [statistics, setStatistics] = useState<readonly UserLicenseStatistics[]>([]);
	const [loadingStatus, setLoadingStatus] = useState<LoadingStatus>("loading");

	const alertManager: AlertManager = useAlerts();
	const [reload, statisticsVersion] = useForceRerender();

	useEffect(
		() => {
			hasImplicitDependencyOn(statisticsVersion);

			const load = async (signal: AbortSignal): Promise<void> => {
				try {
					setLoadingStatus("loading");

					const statistics: readonly UserLicenseStatistics[] = await getStatistics(signal);

					setStatistics(
						sortBy(
							statistics.filter(x => x.regularUserCount + x.guestUserCount > 0),
							x => statisticsOrder.indexOf(x.userLicense)
						)
					);

					setLoadingStatus("success");
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}

					setLoadingStatus("error");
					alertManager.showError(localize("_GeneralErrorMessage"));
					console.error(e);
				}
			};

			const cancellation = new AbortController();
			load(cancellation.signal);
			return () => cancellation.abort();
		},
		[alertManager, statisticsVersion]
	);

	if (loadingStatus === "loading") {
		return null;
	}

	return (
		<SettingsPage.Section title={localize("_Packages")}>
			{loadingStatus === "error"
				? (
					<NoResults
						title={localize("_UnableToRetrieveItems")}
						icon="gear"
						message={localize("_WeCantRetrieveAnyItemsAtThisMomentPleaseRetry")}
						onRetry={reload}
						height="fill"
					/>
				)
				: (
					<TableView
						items={statistics}
						itemKey="userLicense"
						columns={[
							{
								title: localize("_Title"),
								trackingId: "title",
								minWidth: 140
							},
							{
								title: localize("_RegularUsers"),
								trackingId: "regular-users",
								minWidth: 200
							},
							{
								title: localize("_GuestUsers"),
								trackingId: "guest-users",
								minWidth: 200
							}
						]}
						trackingId="modules-table"
					>
						{item => (
							<TableView.Row
								trackingId={item.userLicense.toString()}
							>
								<TableView.Cell>
									{getUserLicenseName(item.userLicense)}
								</TableView.Cell>
								<TableView.Cell>{item.regularUserCount}</TableView.Cell>
								<TableView.Cell>{item.guestUserCount}</TableView.Cell>
							</TableView.Row>
						)}
					</TableView>
				)
			}
		</SettingsPage.Section>
	);
};

UserLicenseSection.displayName = "UserLicenseSection";

const statisticsOrder: UserLicense[] = [
	UserLicense.Simplify,
	UserLicense.Connect
];

type LoadingStatus = "success" | "loading" | "error";
